import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  signal
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { ApprovalState, Client, FileParameter, ReportType, RiskEvaluationDto } from '@fdm/api-client';
import { LoaderService } from '@fdm/services';
import { FileUploadComponent } from '@fdm/shared';
import { getErrorMessage } from '@fdm/utils';
import { ToastrService } from 'ngx-toastr';
import {
  catchError,
  combineLatest,
  filter,
  finalize,
  first,
  of,
  switchMap,
  tap
} from 'rxjs';
import { FormValidationService, NavigationService, ReportFormPage, ReportsBulkSaveService, ReportTask } from '../../../../services';
import { RiskEvaluationCreatePayload } from './models';
import { RiskEvaluationApiService } from './services/risk-evaluation-api.service';
import { ReportForm } from '../../report-form';
import { AngularEditorModule, AngularEditorConfig } from '@kolkov/angular-editor';
import { createEditorConfigs } from '../../shared/richtext-editor-config-helper';

const DEFAULT_SCORE = 10;

enum FDMRecordStatus {
  ACTIVE_DM,
  ACTIVE_FM,
  ACTIVE_ALL,
  INACTIVE_ALL,
}

@Component({
  selector: 'app-risk-evaluation',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,

    // Material
    MatIconModule,
    MatButtonModule,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,

    FileUploadComponent,
    AngularEditorModule
  ],
  templateUrl: './risk-evaluation.component.html',
  styleUrls: ['./risk-evaluation.component.scss'],
  providers: [RiskEvaluationApiService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RiskEvaluationComponent extends ReportForm implements OnInit {

  private editorConfigs = createEditorConfigs(5, 'risk-eval');
  configHealthSafety: AngularEditorConfig = this.editorConfigs[0];
  configEnvironmental: AngularEditorConfig = this.editorConfigs[1];
  configReputation: AngularEditorConfig = this.editorConfigs[2];
  configAsset: AngularEditorConfig = this.editorConfigs[3];
  configRiskAssessment: AngularEditorConfig = this.editorConfigs[4];

  private _api = inject(RiskEvaluationApiService);
  private _apiClient = inject(Client);
  private _route = inject(ActivatedRoute);
  private _router = inject(Router);
  private _navigationService = inject(NavigationService);
  private _fb = inject(FormBuilder);
  private _toastrService = inject(ToastrService);
  private _loader = inject(LoaderService);
  private _bulkSave = inject(ReportsBulkSaveService);
  private _cdr = inject(ChangeDetectorRef);
  private _formValidationService = inject(FormValidationService);

  get scores() {
    return this.form.get('scores') as FormArray<FormGroup<any>>;
  }

  get attachRiskEvaluationForm() {
    return this.form.get('attachRiskEvaluationForm') as FormControl<FileParameter | null>;
  }

  get path() {
    return this.form.get('attachRiskEvaluationFormPath') as FormControl<string | null>;
  }

  get id() {
    return this.form.get('id') as FormControl<number | null>;
  }

  public get step() {
    return ReportFormPage.RISK_EVALUATION;
  }

  protected displayNames: { [key: string]: string } = {
    healthAndSafetyComment: 'Health and Safety Comment',
    environmentalComment: 'Environmental Comment',
    reputationComment: 'Reputation Comment',
    assetComment: 'Asset Comment',
    riskAssessmentComments: 'Risk Assessment Comments',
  };

  form = this._fb.group({
    scores: this._fb.array([
      this._fb.group({
        id: [null],
        category: ['HealthAndSafety'],
        title: ['Health and Safety'],
        consequence: [null, Validators.required],
        likelihood: [null, Validators.required],
        rankedScore: [DEFAULT_SCORE],
        generalTabId: [null],
      }),
      this._fb.group({
        id: [0],
        category: ['Environmental'],
        title: ['Environmental'],
        consequence: [null, Validators.required],
        likelihood: [null, Validators.required],
        rankedScore: [DEFAULT_SCORE],
        generalTabId: [null],
      }),
      this._fb.group({
        id: [null],
        category: ['Reputation'],
        title: ['Reputation'],
        consequence: [null, Validators.required],
        likelihood: [null, Validators.required],
        rankedScore: [DEFAULT_SCORE],
        generalTabId: [null],
      }),
      this._fb.group({
        id: [null],
        category: ['Asset'],
        title: ['Asset'],
        consequence: [null, Validators.required],
        likelihood: [null, Validators.required],
        rankedScore: [DEFAULT_SCORE],
        generalTabId: [null],
      }),
    ]),
    id: [null],
    totalScore: [DEFAULT_SCORE],
    healthAndSafetyComment: ['', [Validators.maxLength(20000)]],
    environmentalComment: ['', [Validators.maxLength(20000)]],
    reputationComment: ['', [Validators.maxLength(20000)]],
    assetComment: ['', [Validators.maxLength(20000)]],
    riskAssessmentComments: ['', [Validators.maxLength(20000)]],
    attachRiskEvaluationForm: [null],
    attachRiskEvaluationFormPath: [''],
  });

  options = signal<number[]>([1, 2, 3, 4, 5, 6]);

  readonly textAreaRows = signal<number>(3);

  file: FileParameter | null = null;

  private _generalTabId: string | null = null;

  private _closeReport = toSignal(this._store.closeReport$);
  private currentStep = toSignal(this._store.page$);
  private _general = toSignal(this._store.general$);

  override ngOnInit() {
    super.ngOnInit();

    this._route.paramMap
      .pipe(
        filter(() => !this.form.dirty),
        switchMap((params) => {
          this._generalTabId = params.get('id');

          this._loader.startLoading();

          return this._generalTabId
            ? this._getDataById(this._generalTabId)
            : of(null);
        })
      )
      .subscribe(() => {
        this._loader.stopLoading();
        this._cdr.markForCheck();
      });

    this.scores.valueChanges
      .pipe(takeUntilDestroyed(this._destroy))
      .subscribe(() => {
        this._calcScores();
        this._cdr.markForCheck();
      });

    this._store.page$
      .pipe(
        filter((page) => page === this.step),
        takeUntilDestroyed(this._destroy)
      )
      .subscribe(() => {
        this._store.setPageValidity({ page: this.step, isStepValid: this.form.valid });
        this._cdr.markForCheck();
      });

    this.form.valueChanges
      .pipe(
        filter(() => this.step === this.currentStep()),
        takeUntilDestroyed(this._destroy)
      )
      .subscribe(() => {
        const isFormValid = this.form.valid;

        this._store.setPageValidity({ page: this.step, isStepValid: isFormValid });

        if (isFormValid) {
          const payload: ReportTask = {
            page: this.step,
            request: this._save()
          };

          if (this._bulkSave.includes(this.step)) {
            this._bulkSave.update(payload);
          } else {
            this._bulkSave.add(payload);
          }
        }

        this._cdr.markForCheck();
      });

    this._store.general$
      .pipe(takeUntilDestroyed(this._destroy))
      .subscribe(general => {
        if (
          // !this._formValidationService.isFormExists(this.step) &&
          general?.currentState === ApprovalState.SubmittedToOpsAdvisor &&
          (
            this._store.reportType() === ReportType.IntegrityDeficiency ||
            this._store.reportType() === ReportType.ReliabilityDeficiency ||
            this._store.reportType() === ReportType.TaskDeferral
          )
        ) {
          this._formValidationService.registerForm(this.step, this.form);
        }
      });
  }
  protected override onPolicyOrFormStateChanged() {
    this.richTextFieldPolicy();
  }
  richTextFieldPolicy() {
    this.configHealthSafety.editable = this.isFieldEditable('healthAndSafetyComment');
    this.configEnvironmental.editable = this.isFieldEditable('environmentalComment');
    this.configReputation.editable = this.isFieldEditable('reputationComment');
    this.configAsset.editable = this.isFieldEditable('assetComment');
    this.configRiskAssessment.editable = this.isFieldEditable('riskAssessmentComments');
  }

  private _save() {
    const payload: RiskEvaluationCreatePayload = {
      ...this.form.value,
      healthAndSafetyComment: this.form.value.healthAndSafetyComment ?? '',
      environmentalComment: this.form.value.environmentalComment ?? '',
      reputationComment: this.form.value.reputationComment ?? '',
      assetComment: this.form.value.assetComment ?? '',
      riskAssessmentComments: this.form.value.riskAssessmentComments ?? '',
      scores:
        this.form.value.scores?.map((item) =>
          RiskEvaluationDto.fromJS({
            ...item,
            generalTabId: this._generalTabId,
          })
        ) ?? [],
    };

    return this._handleSave(payload)
      .pipe(
        tap(() => {
          this._store.reset();

          if (this._closeReport()) {
            this._navigationService.navigateToListPage();
          }
        }),
        switchMap(() =>
          this._generalTabId ? this._getDataById(this._generalTabId) : of(null)
        ),
        catchError((err) => {
          this._store.reset();
          return of(err);
        }),
      );
  }

  getErrorMessage(fieldName: string): string | null {
    return getErrorMessage(this.form, fieldName, this.displayNames);
  }

  public getFile(file: FileParameter | null): void {
    this.file = file;
    this.attachRiskEvaluationForm.setValue(file);
  }

  public download() {
    const path = this.form.value.attachRiskEvaluationFormPath;

    if (!path) {
      return;
    }

    this._api.download(path).subscribe(() => {
      this._cdr.markForCheck();
    });
  }

  private _handleSave(payload: RiskEvaluationCreatePayload) {
    return this.id.value
      ? this._api
        .update(payload)
        .pipe(
          tap(() => {
            // hide notification for closing
            if (this._general()?.currentState !== ApprovalState.ValidationAndVerificationCompleted) {
              this._toastrService.success('Risk Evaluation updated successfully.')
            }
          })
        )
      : this._api
        .create(payload)
        .pipe(
          tap(() => this._toastrService.success('Risk Evaluation saved successfully.'))
        );
  }

  private _getDataById(generalTabId: string) {
    return this._api.getRiskEvaluationById(generalTabId).pipe(
      tap((data) => {
        this._store.setRisk(data);
        
        const scores = data?.riskEvaluationDtos?.length 
          ? this.scores.value.map(score => data.riskEvaluationDtos.find(item => item.category === score.category)) 
          : [];

        this.form.patchValue({
          ...data.riskCommentDto,
          scores
        } as any, { emitEvent: false });

        this._calcScores();
      }),
      catchError(err => {
        return of(err);
      })
    );
  }

  public removeFile() {
    const formData = this.form.value;
    const id = formData.id ? +formData.id : null;
    const path = formData.attachRiskEvaluationFormPath;

    if (!id || !path) {
      return;
    }

    this._loader.startProcessing();
    this._apiClient.file(id, path)
      .pipe(
        first(),
        switchMap(() => this._generalTabId ? this._getDataById(this._generalTabId) : of(null)),
        catchError(err => {
          this._loader.stopProcessing();
          return of(err);
        }),
        finalize(() => {
          this._cdr.markForCheck();
          this._loader.stopProcessing();
        })
      )
      .subscribe();
  }

  private _calcScores(): void {
    const scores = this.scores.value ?? [];
    let minScore = DEFAULT_SCORE;

    scores.forEach((value, i) => {
      const consequence = value.consequence ?? 0;
      const likelihood = value.likelihood ?? 0;
      const total = consequence + likelihood;
      const score = total > DEFAULT_SCORE ? DEFAULT_SCORE : total - 1;

      if (score > 0 && consequence && likelihood) {
        this.scores.controls[i].get('rankedScore')?.setValue(score, { emitEvent: false });

        if (minScore > score) {
          minScore = score;
        }
      }

      this.form.get('totalScore')?.setValue(minScore, { emitEvent: false });
    });
  }
}
